export interface Contact {
  name: string;
  email: string;
  phone: string;
}

export interface Statistics {
  upgradeServers: number;
  upgradeWorkstations: number;
  doNotUpgrade: number;
}

export interface AcceptedBy {
  name: string;
  email: string;
  timestamp: number;
}

export type PracticeUpgradeStatus =
  | ''
  | 'Scheduled'
  | 'Cancelled'
  | 'Cancelled (to reschedule)'
  | 'Rescheduled'
  | 'Upgraded (Success)'
  | 'Upgraded (Error)'
  | 'Aborted';

export type AgentStatus =
  | 'UPGRADE_NOT_SCHEDULED'
  | 'UPGRADE_PENDING'
  | 'UPGRADE_COMPLETE'
  | 'UPGRADE_FAILED'
  | 'OFFLINE'
  | 'REFRESH_IN_PROGRESS';

export type UpgradePhase =
  | 'fileDistribution'
  | 'preUpgradeReboot'
  | 'checkScheduled'
  | 'serverMsiLogPath'
  | 'serverMsiLog'
  | 'serverCsUpdateLog'
  | 'serverPreReboot'
  | 'removeRunOnceEntry'
  | 'serverReboot'
  | 'csWebappCleanup'
  | 'workstationsMsiLogPath'
  | 'workstationsMsiLog'
  | 'complete'
  | 'error';

export interface CsVersion {
  version: string;
  versionForSorting?: string;
  installerName: string;
  installerZipName: string;
  installerArgs: string;
  installerSize: number;
  minimalUpgradeVersion: string;
  serverRam: number;
  serverDiskSize: number;
  workstationRam: number;
  workstationDiskSize: number;
  osPattern: string;
  cpuPattern: string;
  qualificationAPs: string;
  rebootTimeout: number;
  default: boolean;
  isDeletable: boolean;
}

export type CsVersionValidation = Partial<Record<keyof CsVersion, string>>;

export type PracticeReportType =
  | 'Upgrade Summary Report'
  | 'Upgrade File Download Results'
  | 'Qualification Check Failed'
  | 'Workstation Upgrade Results'
  | 'Upgrade Completion Report'
  | 'Acknowledgments';

export interface PracticeReport {
  type: PracticeReportType;
  path: string;
  timestamp: number;
}

export interface PracticeConfig {
  sapId: string;
  practiceName: string;
  upgradeStatus?: PracticeUpgradeStatus;
  scheduleUpgradeTimestamp?: number;
  utcOffset?: number;
  timeZoneName?: string;
  scheduleTimestamp?: number;
  scheduledBy?: string;
  statistics?: Statistics;
  contacts?: Contact[];
  successAgentIds?: string[];
  failedAgentIds?: string[];
  scheduleError?: 'Kaseya' | 'Communication';
  lockedByEmail?: string;
  lockedByName?: string;
  lockEndTimestamp?: number;
  isLocked?: boolean;
  serverAgentId?: string;
  serverStatus?: AgentStatus;
  acknowledgementsLastAccepted?: AcceptedBy;
  toCsVersion: CsVersion;
  skippingWorkstationsAllowed?: boolean;
  upgradeProcess?: {
    queued: boolean;
    phase: UpgradePhase;
    phaseStartedAt: number;
    outcome?: string;
  };
  upgradeDisplayState?: {
    title: string;
    hint: string;
    type: 'info' | 'warning' | 'error';
    details?: string;
  };
  reports?: PracticeReport[];
}

export const AuditStatus = {
  QUALIFIED: 'Q',
  REQUIRED: 'R',
  PENDING: 'P',
  UNQUALIFIED: 'U',
  ERROR: 'E',
} as const;

export const UpgradeStatus = {
  UPGRADE: 'U',
  DO_NOT_UPGRADE: 'DNU',
  UPGRADE_MANUALLY_APPROVED: 'UMA',
} as const;

export interface AgentQualifications {
  os?: boolean;
  processor?: boolean;
  memory?: boolean;
  csVersion?: boolean;
  csDrive?: boolean;
  csDiskSize?: boolean;
  cDiskFreeSpace?: boolean;
  connectivity?: boolean;
  powershell?: boolean;
}

export interface AgentData {
  checked?: boolean;
  auditLoading?: boolean;
  rowLoading?: boolean;
  agentId: string;
  sapId: string;
  lastCheckInTime: string;
  online: number;
  status: (typeof UpgradeStatus)[keyof typeof UpgradeStatus];
  type?: string;
  label?: string;
  name: string;
  auditStatus?: (typeof AuditStatus)[keyof typeof AuditStatus];
  os: string;
  processor: string;
  memoryMb?: number;
  csVersion?: string;
  csDrive?: string;
  csPath?: string;
  csDiskSizeMb?: number;
  csDiskFreeSpaceMb?: number;
  cDiskSizeMb?: number;
  shareSizeMb?: number;
  cDiskFreeSpaceMb?: number;
  cDiskRequiredFreeSpace?: number;
  cachedTimestamp: number;
  utcOffset?: number;
  timeZoneName?: string;
  lastAuditTimestamp?: number;
  lastQualifiedTimestamp?: number;
  internetStatistics?: string;
  qualifications?: AgentQualifications;
  win11Readiness?: boolean;
}

export interface RoleApi {
  roleId: string;
  roleLevel: number;
  resourcePermissions: ResourcePermission[];
}

export interface ResourcePermission {
  resourceId: string;
  permission: string;
  type?: string;
  active?: boolean;
}

export interface Role {
  sapId?: string;
  name: string;
  description?: string;
  level: number;
  global: boolean;
  hidden: boolean;
  default: boolean;
}

export interface User {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  createdAt?: string;
  lastAccessAt?: string;
  roles: Role[];
}

export interface AuditEvent {
  id: string;
  date: Date;
  event: string;
  type: string;
  sapId: string;
  userEmail: string;
  userRole: string;
  agentId: string;
  agentName: string;
  agentStatus: string;
  agentAuditStatus?: string;
  agentLabel?: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  requestUrl: string;
  request: string;
  response: string;
  success: boolean;
  managementUserEmail: string;
  managementUserFirstName: string;
  managementUserLastName: string;
  managementUserPhone: string;
  managementUserRoles: string;
}

export interface AgentsKaseyaInfo {
  machineGroupName?: string;
  vsaHost: string;
}

export const isAgentUpgradeQualified = (a: AgentData): boolean =>
  (a.auditStatus === AuditStatus.QUALIFIED &&
    (a.status === UpgradeStatus.UPGRADE || a.status === UpgradeStatus.UPGRADE_MANUALLY_APPROVED)) ||
  (a.auditStatus === AuditStatus.UNQUALIFIED &&
    a.status === UpgradeStatus.UPGRADE_MANUALLY_APPROVED);

export const isUpgradeScheduled = (practice?: PracticeConfig): boolean =>
  practice?.upgradeStatus === 'Scheduled' || practice?.upgradeStatus === 'Rescheduled';
